import { useEffect, useState } from "react";
import { Users, ExternalLink, Lock, CheckCircle2, Clock, UserPlus } from "lucide-react";
import { api } from "@/lib/api";

const STATUS = {
  ok: { label: "Surveillé", cls: "chip-live", Icon: CheckCircle2 },
  empty: { label: "Rien de récent", cls: "chip-warn", Icon: Clock },
  private: { label: "Profil privé", cls: "chip-tt", Icon: Lock },
};

function timeAgo(iso) {
  if (!iso) return "";
  const h = Math.round((Date.now() - new Date(iso).getTime()) / 3600000);
  return h < 24 ? `il y a ${Math.max(h, 1)} h` : `il y a ${Math.round(h / 24)} j`;
}

function BloggerRow({ b, idx }) {
  const st = STATUS[b.status] || STATUS.empty;
  const lp = b.last_post;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-[#e4e6eb] last:border-0" data-testid={`blogger-row-${idx}`}>
      <div className="w-9 h-9 rounded-full bg-[#e7f3ff] text-[#1877f2] font-display font-bold flex items-center justify-center shrink-0">
        {(b.name || "?").charAt(0).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <a href={b.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-sm hover:text-[#1877f2] truncate">
            {b.name || (b.status === "private" ? "Profil privé" : "Profil découvert")}
          </a>
          <span className={`chip ${st.cls}`} data-testid={`blogger-status-${idx}`}><st.Icon className="w-3 h-3" /> {st.label}</span>
          {b.via && <span className="chip"><UserPlus className="w-3 h-3" /> ami de {b.via}</span>}
        </div>
        {lp ? (
          <a href={lp.url} target="_blank" rel="noopener noreferrer" className="mt-1 block text-xs text-[color:var(--bcs-text-mut)] hover:text-[#1877f2]">
            <span className="line-clamp-1">{lp.text || "Publication photo/vidéo"}</span>
            <span className="font-mono-ui text-[10.5px] text-[color:var(--bcs-text-dim)]">
              {timeAgo(lp.posted_at)} · {lp.likes} j'aime · {lp.comments} comm. · {lp.shares} partages <ExternalLink className="inline w-3 h-3" />
            </span>
          </a>
        ) : (
          <div className="mt-1 text-xs text-[color:var(--bcs-text-dim)]">
            {b.status === "private" ? "Facebook bloque la lecture de ce profil." : "Aucune publication ces dernières 48 h."}
          </div>
        )}
      </div>
    </div>
  );
}

export default function MonitoredBloggers({ refreshKey }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.veilleProfiles().then(r => setRows(Array.isArray(r) ? r : [])).catch(() => {}); }, [refreshKey]);
  const ok = rows.filter(r => r.status === "ok").length;
  return (
    <section className="card-surface p-6" data-testid="monitored-bloggers">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-[color:var(--bcs-accent)]" />
          <h2 className="font-display text-xl font-bold">Blogueurs surveillés</h2>
        </div>
        <span className="chip chip-fb" data-testid="bloggers-count">{ok}/{rows.length} actifs</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-[color:var(--bcs-text-mut)] py-6">Lancez une veille pour voir le statut de chaque blogueur.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8">
          {rows.map((b, i) => <BloggerRow key={b.url} b={b} idx={i} />)}
        </div>
      )}
    </section>
  );
}
